<script setup lang="ts">
import type { ProFootSlots } from '../interfaces';
import type { VNode } from 'vue';
import { bem } from '@hlx/use';
import { BEM_XOM_NS } from '@inn/const';
import { computed, ref, watchEffect, h } from 'vue';
import { Pagination as APagination, Input } from 'ant-design-vue';
import { useLayoutState } from '../composables';
import XomLayoutCopyright from './copyright.vue';

defineOptions({ name: 'XomProLayoutFooter' });
const props = defineProps<{
  setRef: (el: HTMLElement | null) => void;
  current: number;
  pageSize: number;
  quickPage?: boolean;
  total?: number;
  pageSizeOptions?: Array<number | string>;
  simple?: boolean;
  showTotal?: (total: number, range: [number, number]) => string | VNode | VNode[];
  hideOnSinglePage?: boolean;
  defaultPageSize?: number;
  showLessItems?: boolean;
  showQuickJumper?: boolean;
  showSizeChanger?: boolean;
  pageChange?: (page: number) => void;
  pageSizeChange?: (pageSize: number) => void;
  isFooterShow?: boolean;
}>();

defineSlots<ProFootSlots>();
const _footerRef = ref<HTMLElement>();
const { isDark, effective, copyright, icp } = useLayoutState();
const ns = bem('layout-footer', BEM_XOM_NS);
const isFixedBody = computed(() => effective.body === 'fixed' && effective.layout !== 'mix');

watchEffect(() => {
  if (_footerRef.value) {
    props.setRef(_footerRef.value);
  }
});

const isEmptyTeleport = computed(() => {
  const footerRef = _footerRef.value;
  return footerRef && footerRef.children.length === 0;
});

const _page = computed({
  get: () => props.current,
  set: (newly) => {
    props.pageChange?.(newly);
  },
});
const _size = computed({
  get: () => props.pageSize,
  set: (newly) => {
    props.pageSizeChange?.(newly);
  },
});

const pageSizeChange = (_page: number, size: number) => {
  props.pageChange?.(size);
};

const antPageOptions = computed(() => {
  return {
    total: props.total || 0,
    pageSizeOptions: props.pageSizeOptions || ['60', '120', '200'],
    simple: !!props.simple,
    showTotal: props.showTotal || ((total: number) => `共 ${total} 条`),
    hideOnSinglePage: props.hideOnSinglePage,
    showSizeChanger: !!props.showSizeChanger,
    showQuickJumper: props.showQuickJumper,
    showLessItems: props.showLessItems,
    defaultPageSize: props.defaultPageSize,
  };
});

const pageShowRender = (params: {
  page: number;
  type: 'page' | 'prev' | 'next' | 'jump-prev' | 'jump-next';
  originalElement: any;
}) => {
  const { page, type, originalElement } = params;
  if (type === 'page') {
    return page === _page.value
      ? h(Input, {
          value: page,
          style: { width: '40px' },
          class: ns.se('footer', 'page-input'),
          onPressEnter: (e) => {
            const value = (e?.target as HTMLInputElement).value;
            if (Number(value) !== page && !Number.isNaN(value)) {
              _page.value = Number(value);
              props.pageChange?.(Number(value));
            }
          },
        })
      : null;
  }
  return type === 'next' || type === 'prev' ? originalElement : null;
};
</script>

<template>
  <a-layout-footer
    :class="[
      ns.b(),
      ns.is('dark', isDark),
      ns.is('fixed', effective.fixedFoot),
      ns.is('fixed-body', isFixedBody),
    ]"
  >
    <div id="footer-ref" ref="_footerRef" :class="[ns.e('content'), ns.e('content-ref')]" />
    <div
      v-if="isEmptyTeleport && isFooterShow"
      id="footer-page"
      :class="[ns.e('content'), ns.e('content-normal')]"
    >
      <slot name="footer">
        <a-pagination
          v-model:current="_page"
          v-model:page-size="_size"
          class="pageKlass"
          :class="ns.se('footer', 'no-total-page')"
          v-bind="antPageOptions"
          :total="total"
          :simple="false"
          :item-render="pageShowRender"
          @change="pageChange"
          @show-size-change="pageSizeChange"
        />
      </slot>
    </div>
    <ul :class="ns.e('links')" />

    <div v-if="copyright !== false && icp !== false" :class="ns.e('copyright')">
      <slot name="copyright">
        <xom-layout-copyright />
      </slot>
      <slot name="icp" />
    </div>
  </a-layout-footer>
</template>

<style scoped lang="scss">
@use '@inn/assets/scss/vars';
@use '@inn/assets/scss/mixins';

@include mixins.b(layout-footer) {
  // --height: calc(var(--xom-layout-footer-height) - var(--xom-padding-xs) * 2);
  display: block;
  flex-flow: column;
  padding: 0;
  background-color: var(--xom-color-bg-container);

  // overflow: hidden;

  @include mixins.e(links) {
    margin: 0;
    list-style-type: none;
  }

  @include mixins.e(content) {
    // height: var(--height);
  }

  @include mixins.e(content-normal) {
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: center;
    justify-content: flex-end;
    height: 52px;
    padding: 8px 20px;
    background-color: transparent;
  }

  @include mixins.e(content-ref) {
    &:has(footer) {
      & + #footer-page {
        display: none;
      }
    }

    &:has(div) {
      & + #footer-page {
        display: none;
      }
    }
  }

  @include mixins.e(content-hide) {
    display: none;
  }

  @include mixins.when(fixed) {
    display: flex;
    flex-flow: column;

    // height: var(--xom-layout-footer-height);
    // padding: var(--xom-padding-xs) var(--xom-padding);
    line-height: var(--height);
    background-color: var(--xom-color-bg-container);

    // box-shadow: 0 -1px 4px rgb(0 21 41 / 8%);
  }

  @include mixins.when(dark) {
    @include mixins.when(fixed) {
      // box-shadow: 0 -3px 8px 0 rgb(13 13 13 / 75%);
    }
  }

  @include mixins.when(fixed-body) {
    @include mixins.e('content') {
      width: 100%;
      max-width: var(--xom-layout-body-fixed-width);
      margin: 0 auto;
    }
  }
}
</style>
